import React, { useRef, useEffect, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';

const OrganicSideCanvas = ({ canvasRef }) => {
  const animRef = useRef(null);
  const timeRef = useRef(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    timeRef.current += 0.005;
    const t = timeRef.current;

    ctx.clearRect(0, 0, w, h);

    // Flowing organic curves radiating from center-right edge
    for (let i = 0; i < 8; i++) {
      ctx.beginPath();
      const startY = h * 0.2 + i * (h * 0.08);
      ctx.moveTo(w, startY);
      for (let x = w; x >= 0; x -= 3) {
        const progress = (w - x) / w;
        const amplitude = progress * 40 * (1 + Math.sin(i * 0.7) * 0.5);
        const y = startY
          + Math.sin(x * 0.012 + t + i * 0.9) * amplitude
          + Math.sin(x * 0.025 + t * 1.4 + i) * (amplitude * 0.4);
        ctx.lineTo(x, y);
      }
      ctx.strokeStyle = `rgba(224, 90, 71, ${0.3 - i * 0.025})`;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Organic particles drifting toward center
    for (let i = 0; i < 15; i++) {
      const angle = t * 0.3 + i * (Math.PI * 2 / 15);
      const radius = 60 + Math.sin(t * 0.5 + i * 2) * 30 + i * 8;
      const px = w - radius * Math.cos(angle) * 0.6;
      const py = h / 2 + radius * Math.sin(angle) * 0.8;
      const size = 1.5 + Math.sin(t + i * 1.5) * 0.8;

      ctx.beginPath();
      ctx.arc(px, py, size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(212, 163, 115, ${0.5 + Math.sin(t + i) * 0.25})`;
      ctx.fill();
    }

    // Ink blots / organic shapes
    for (let i = 0; i < 4; i++) {
      const cx = w * (0.3 + i * 0.18);
      const cy = h * 0.5 + Math.sin(t * 0.4 + i * 2) * 40;
      ctx.beginPath();
      for (let a = 0; a < Math.PI * 2; a += 0.2) {
        const r = 8 + Math.sin(a * 4 + t + i * 1.5) * 5;
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r;
        if (a === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fillStyle = `rgba(224, 90, 71, ${0.1 + Math.sin(t * 0.8 + i) * 0.04})`;
      ctx.fill();
    }

    animRef.current = requestAnimationFrame(draw);
  }, [canvasRef]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  return null;
};

const DigitalSideCanvas = ({ canvasRef }) => {
  const animRef = useRef(null);
  const timeRef = useRef(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    timeRef.current += 0.006;
    const t = timeRef.current;

    ctx.clearRect(0, 0, w, h);

    // Grid emanating from left edge
    const gridSize = 30;
    for (let x = 0; x < w; x += gridSize) {
      const fadeIn = Math.min(1, x / (w * 0.6));
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.strokeStyle = `rgba(0, 240, 255, ${0.1 * fadeIn})`;
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }
    for (let y = 0; y < h; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.strokeStyle = `rgba(0, 240, 255, 0.08)`;
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }

    // Data streams / scan lines moving toward center
    for (let i = 0; i < 6; i++) {
      const y = h * 0.15 + i * (h * 0.12);
      const scanX = ((t * 80 + i * 120) % (w + 60)) - 30;

      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(scanX, y);
      ctx.strokeStyle = `rgba(0, 240, 255, ${0.35 - i * 0.04})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Scan head glow
      ctx.beginPath();
      ctx.arc(scanX, y, 3, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(0, 240, 255, ${0.8 + Math.sin(t * 3 + i) * 0.2})`;
      ctx.fill();
    }

    // Circuit nodes
    for (let i = 0; i < 10; i++) {
      const nx = (i * 0.09 + 0.08) * w;
      const ny = h * 0.5 + Math.sin(i * 1.2) * (h * 0.3);
      const pulse = Math.sin(t * 2 + i * 1.5);

      ctx.beginPath();
      ctx.rect(nx - 3, ny - 3, 6, 6);
      ctx.strokeStyle = `rgba(0, 240, 255, ${0.45 + pulse * 0.25})`;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Connection to next node
      if (i < 9) {
        const nx2 = ((i + 1) * 0.09 + 0.08) * w;
        const ny2 = h * 0.5 + Math.sin((i + 1) * 1.2) * (h * 0.3);
        ctx.beginPath();
        ctx.moveTo(nx + 3, ny);
        ctx.lineTo(nx2 - 3, ny2);
        ctx.strokeStyle = `rgba(0, 240, 255, 0.15)`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }
    }

    animRef.current = requestAnimationFrame(draw);
  }, [canvasRef]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  return null;
};

export const LogoShowcase = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-80px' });
  const leftCanvasRef = useRef(null);
  const rightCanvasRef = useRef(null);

  useEffect(() => {
    const resize = () => {
      const container = sectionRef.current;
      if (!container) return;
      const w = container.offsetWidth / 2;
      const h = 320;
      if (leftCanvasRef.current) {
        leftCanvasRef.current.width = w;
        leftCanvasRef.current.height = h;
      }
      if (rightCanvasRef.current) {
        rightCanvasRef.current.width = w;
        rightCanvasRef.current.height = h;
      }
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="logo-showcase"
      className="relative py-16 md:py-24 overflow-hidden"
      style={{ background: '#070709' }}
    >
      {/* Split canvases */}
      <div className="absolute inset-0 flex">
        {/* Human / organic side */}
        <div className="w-1/2 relative">
          <canvas ref={leftCanvasRef} className="absolute inset-0 w-full h-full" />
          <OrganicSideCanvas canvasRef={leftCanvasRef} />
        </div>
        {/* AI / digital side */}
        <div className="w-1/2 relative">
          <canvas ref={rightCanvasRef} className="absolute inset-0 w-full h-full" />
          <DigitalSideCanvas canvasRef={rightCanvasRef} />
        </div>
      </div>

      {/* Center divider pulse */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px z-10">
        <motion.div
          className="w-full h-full"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, rgba(224,90,71,0.7) 30%, rgba(0,240,255,0.7) 70%, transparent 100%)',
          }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Logo container */}
      <div className="relative z-20 flex items-center justify-center" style={{ minHeight: '280px' }}>
        {/* Outer rotating ring - human side (organic, wobbly) */}
        <motion.div
          className="absolute"
          animate={isInView ? { rotate: 360 } : {}}
          transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
        >
          <svg width="220" height="220" viewBox="0 0 220 220" fill="none">
            <circle
              cx="110" cy="110" r="105"
              stroke="#E05A47"
              strokeWidth="1"
              strokeDasharray="6 8 2 12"
              opacity="0.6"
            />
          </svg>
        </motion.div>

        {/* Outer rotating ring - AI side (geometric, precise) */}
        <motion.div
          className="absolute"
          animate={isInView ? { rotate: -360 } : {}}
          transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
        >
          <svg width="260" height="260" viewBox="0 0 260 260" fill="none">
            <circle
              cx="130" cy="130" r="125"
              stroke="#00F0FF"
              strokeWidth="1"
              strokeDasharray="2 6"
              opacity="0.5"
            />
          </svg>
        </motion.div>

        {/* Inner pulsing glow */}
        <motion.div
          className="absolute rounded-full"
          style={{
            width: 180,
            height: 180,
            background: 'radial-gradient(circle, rgba(224,90,71,0.12) 0%, rgba(0,240,255,0.08) 50%, transparent 70%)',
          }}
          animate={isInView ? { scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] } : {}}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* The logo itself */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          {/* Subtle backdrop */}
          <div
            className="absolute inset-0 -m-4 rounded-sm"
            style={{
              background: 'radial-gradient(circle, rgba(7,7,9,0.8) 40%, transparent 70%)',
            }}
          />
          <img
            src="/logo.svg"
            alt="Turinga Dev - Human + Machine"
            className="relative w-28 h-28 md:w-36 md:h-36"
            data-testid="logo-showcase-image"
          />
        </motion.div>

        {/* Side labels */}
        <motion.span
          initial={{ opacity: 0, x: -20 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="absolute left-6 md:left-12 text-human-primary text-xs md:text-sm italic tracking-wide"
        >
          analog
        </motion.span>
        <motion.span
          initial={{ opacity: 0, x: 20 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="absolute right-6 md:right-12 text-ai-primary text-xs md:text-sm uppercase tracking-[0.3em] font-semibold"
        >
          digital
        </motion.span>
      </div>
    </section>
  );
};
